import { Fragment, useMemo } from 'react';
import { P2GBox } from '@parcel2go/core-ui';
import { CustomerServiceTag, QuoteApiModel, QuoteOptionApiModel } from '~/lib/api/smartsend/types';
import QuoteService from './QuoteService';
import { P2GDivider } from '@parcel2go/core-ui';
import { QuoteOption } from './QuoteOption';
import { P2GGrid } from '@parcel2go/core-ui';
import { QuoteTagType } from '../utils/QuoteTagType';

import { ShopProximityInner } from '../../item/ShopProximity';
import { CurrentSelectionType } from '../utils/CurrentSelectionType';
import { onlyUnique } from '~/lib/utils/array';
import { PolyFillQuoteOptions } from './QuoteCardBase';

export interface QuoteCardProps {
  quote: QuoteApiModel;
  currentSelection?: CurrentSelectionType;
  estimatedValue?: number;
  onSave: (quote: QuoteApiModel, option: QuoteOptionApiModel) => void;
  isSelected: (o: QuoteOptionApiModel) => boolean;
  editMode: QuoteTagType;
  serviceTags: string[];
  updateServiceTags: (add: CustomerServiceTag[], remove: CustomerServiceTag[]) => void;
  toggleSpecificTag: (tag: CustomerServiceTag) => void;
  maxQuoteOptions: number;
}

export function QuoteCard(props: QuoteCardProps) {
  const options = useMemo(
    () => PolyFillQuoteOptions(props.quote, props.maxQuoteOptions),
    [props.quote, props.maxQuoteOptions]
  );

  const availableExtras = useMemo(() => {
    if (!props.quote?.Options) {
      return [];
    }
    const available = props.quote.Options.flatMap((o) => o.Extras ?? []).map((e) => e.Id);
    const included = props.quote.Options.flatMap((o) => o.AvailableExtras ?? []).map((e) => e.Id);
    return [...available, ...included].filter(onlyUnique);
  }, [props.quote]);

  return (
    <P2GBox sx={(t) => ({ boxShadow: t.shadows[2], borderRadius: t.spacing(1), overflow: 'hidden' })} mb={2}>
      <P2GBox p={1}>
        <QuoteService
          service={props.quote.Service}
          closestPickupLocation={props.quote.ClosestPickupLocation}
          currencyCode={props.quote.CurrencyCode}
          availableExtras={availableExtras}
        />
      </P2GBox>
      <ShopProximityInner
        accountId={props.quote.Service.AccountId}
        country={props.currentSelection?.sender?.CountryIsoCode}
        location={props.currentSelection?.sender?.Postcode}
        locationProvider={props.quote.Service.DropOffProviderCode}
        provider={props.quote.Service.Provider}
        serviceTags={props.quote.Service.Tags}
        wrapper={(c) => (
          <P2GBox pl={1} pr={1} pb={1}>
            {c}
          </P2GBox>
        )}
      />
      <P2GDivider component="hr" />
      <P2GBox p={1} sx={(t) => ({ backgroundColor: t.palette.grey.A100 })}>
        <P2GGrid container direction="column" spacing={1}>
          {options.map((o, i) => (
            <Fragment key={o.Extras.length === 0 ? `${i}` : o.Extras?.map((x) => x.Id).join('')}>
              {i > 0 && (
                <P2GGrid item>
                  <P2GDivider component="hr" />
                </P2GGrid>
              )}
              <P2GGrid item>
                <QuoteOption
                  currencyCode={props.quote.CurrencyCode}
                  estimatedValue={props.estimatedValue}
                  option={o}
                  onSelect={() => props.onSave(props.quote, o)}
                  isSelected={props.isSelected(o)}
                />
              </P2GGrid>
            </Fragment>
          ))}
        </P2GGrid>
      </P2GBox>
    </P2GBox>
  );
}
